import React, { useEffect, useMemo, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { collection as firestoreCollection, doc, getDoc, getDocs } from 'firebase/firestore';
import { functions, db } from '../firebase';
import {
  Typography, TextField, MenuItem, Alert, Box, Paper, Table, TableHead,
  TableRow, TableCell, TableBody,
} from '@mui/material';

const listAllergies = httpsCallable(functions, 'listAllergies');

const LEGACY_VIEW_OPTIONS = [{ id: 'sp26', mode: 'legacy', label: 'sp26 (legacy)' }];

// Matches allergyOptions in src/subForms/InformationForm.js - just display
// ordering, so a recognized allergy always sorts before any free-text one
// that slipped in through Edit Registrants/bulk upload.
const ALLERGY_ORDER = ['dairy free', 'gluten free', 'nut free', 'vegetarian'];

// How many registrants have each kind of allergy (canceled registrants
// excluded server-side - see listAllergies). A registrant with more than one
// allergy is counted once in each bucket. VIEWER_LOCALITY sees only their
// own locality's counts, matching Signups/Payment View/Merchandise.
export default function AllergiesPanel({ scopedLocality }) {
  const [viewOptions, setViewOptions] = useState(LEGACY_VIEW_OPTIONS);
  const [viewingId, setViewingId] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [activeSnap, confsSnap] = await Promise.all([
          getDoc(doc(db, 'settings', 'active')),
          getDocs(firestoreCollection(db, 'conferences')),
        ]);
        const newOptions = confsSnap.docs.map((d) => ({ id: d.id, mode: 'new', label: d.id }));
        const options = [...newOptions, ...LEGACY_VIEW_OPTIONS];
        setViewOptions(options);
        const active = activeSnap.data()?.conferenceId;
        setViewingId(active || options[0]?.id || null);
      } catch (error) {
        console.error('Failed to load conference list:', error);
        setViewOptions(LEGACY_VIEW_OPTIONS);
        setViewingId(LEGACY_VIEW_OPTIONS[0].id);
      }
    })();
  }, []);

  const currentOption = viewOptions.find((o) => o.id === viewingId);

  useEffect(() => {
    if (!viewingId) return;
    (async () => {
      setLoading(true);
      setMessage(null);
      try {
        const params = currentOption?.mode === 'legacy' ? { collection: viewingId } : { conferenceId: viewingId };
        const res = await listAllergies(params);
        setItems(res.data?.items || []);
      } catch (error) {
        console.error('Failed to load allergy data:', error);
        setItems([]);
        setMessage({ type: 'error', text: error.message });
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewingId, viewOptions]);

  const allergyCounts = useMemo(() => {
    const counts = new Map();
    for (const item of items) {
      counts.set(item.allergy, (counts.get(item.allergy) || 0) + 1);
    }
    return [...counts.entries()]
      .map(([allergy, count]) => ({ allergy, count }))
      .sort((a, b) => {
        const ai = ALLERGY_ORDER.indexOf(a.allergy);
        const bi = ALLERGY_ORDER.indexOf(b.allergy);
        if (ai !== -1 && bi !== -1) return ai - bi;
        if (ai !== -1) return -1;
        if (bi !== -1) return 1;
        return a.allergy.localeCompare(b.allergy);
      });
  }, [items]);

  const localityCounts = useMemo(() => {
    const byLocality = new Map();
    for (const item of items) {
      if (!byLocality.has(item.locality)) byLocality.set(item.locality, 0);
      byLocality.set(item.locality, byLocality.get(item.locality) + 1);
    }
    return [...byLocality.entries()]
      .map(([locality, count]) => ({ locality, count }))
      .sort((a, b) => b.count - a.count);
  }, [items]);

  return (
    <Box sx={{ maxWidth: 700 }}>
      {scopedLocality && (
        <Alert severity="info" sx={{ mb: 2 }}>
          You're viewing allergies for <strong>{scopedLocality}</strong> only.
        </Alert>
      )}
      <Box sx={{ mb: 2, maxWidth: 300 }}>
        <TextField
          select fullWidth size="small" label="Viewing conference"
          value={viewingId || ''}
          onChange={(e) => setViewingId(e.target.value)}
        >
          {viewOptions.map((o) => (
            <MenuItem key={o.id} value={o.id}>{o.label || o.id}</MenuItem>
          ))}
        </TextField>
      </Box>

      {message && <Alert severity={message.type} sx={{ mb: 2 }}>{message.text}</Alert>}

      {loading ? <Typography>Loading...</Typography> : items.length === 0 ? (
        <Typography color="text.secondary">No allergies recorded yet for this conference.</Typography>
      ) : (
        <>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Total allergies recorded: <strong>{items.length}</strong> (a registrant with more than one is counted in
            each of their allergies below)
          </Typography>

          <Paper variant="outlined" sx={{ mb: 3, p: 2 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>By allergy</Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Allergy</TableCell>
                  <TableCell align="right">Count</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {allergyCounts.map((row) => (
                  <TableRow key={row.allergy}>
                    <TableCell>{row.allergy}</TableCell>
                    <TableCell align="right">{row.count}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>

          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>By locality</Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Locality</TableCell>
                  <TableCell align="right">Count</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {localityCounts.map((row) => (
                  <TableRow key={row.locality}>
                    <TableCell>{row.locality}</TableCell>
                    <TableCell align="right">{row.count}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        </>
      )}
    </Box>
  );
}
